/**
 * A QR code for a tracking URL.
 *
 * It encodes the short link, not the destination — which is the entire point.
 * A poster with the destination printed on it is untracked; a poster with this
 * on it goes through /r, records the scan as a click like any other, and lands
 * the visitor in the same place. That is what makes a printed campaign
 * measurable with the same machinery as a WhatsApp message.
 *
 * Drawn as one SVG path rather than a few hundred rects, and always black on
 * white regardless of the workspace theme. A QR code inverted for dark mode is
 * a QR code that many scanners refuse — the quiet zone and the contrast
 * polarity are part of the format, not decoration.
 */
import { useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import Icon from '../../lib/icons.jsx';
import { download } from '../../lib/urltracking.js';

/* Four modules of clear space on every side. The specification requires it and
   scanners genuinely fail without it. */
const QUIET = 4;

/** The dark modules as a single SVG path, in module coordinates. */
function encode(text) {
  /* 0 lets the library pick the smallest version that fits; 'M' is the usual
     balance — about 15% of the code can be damaged and still read, which is
     what a printed poster needs. */
  const qr = qrcode(0, 'M');
  qr.addData(String(text));
  qr.make();

  const count = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (qr.isDark(r, c)) d += `M${c + QUIET} ${r + QUIET}h1v1h-1z`;
    }
  }
  return { d, size: count + QUIET * 2, count };
}

export default function QrCode({ url, name = 'tracking-url', size = 180 }) {
  const [saved, setSaved] = useState('');
  const qr = useMemo(() => {
    try {
      return encode(url);
    } catch {
      return null;
    }
  }, [url]);

  if (!qr) return <p className="utbad">That link is too long to encode as a QR code.</p>;

  const svgText = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${qr.size} ${qr.size}" `
    + `width="${qr.size * 8}" height="${qr.size * 8}" shape-rendering="crispEdges">`
    + `<rect width="${qr.size}" height="${qr.size}" fill="#ffffff"/>`
    + `<path d="${qr.d}" fill="#000000"/></svg>`;

  const flash = what => { setSaved(what); setTimeout(() => setSaved(''), 1800); };

  const savePng = () => {
    /* Drawn straight onto the canvas rather than by loading the SVG into an
       image: an SVG data URL taints the canvas in some browsers, and toBlob
       then throws on the very step this exists to perform. */
    const scale = 10;
    const px = qr.size * scale;
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = '#000000';

    const again = qrcode(0, 'M');
    again.addData(String(url));
    again.make();
    for (let r = 0; r < again.getModuleCount(); r++) {
      for (let c = 0; c < again.getModuleCount(); c++) {
        if (again.isDark(r, c)) {
          ctx.fillRect((c + QUIET) * scale, (r + QUIET) * scale, scale, scale);
        }
      }
    }

    canvas.toBlob(blob => {
      if (blob) { download(blob, `${slugName(name)}-qr.png`); flash('PNG saved'); }
    }, 'image/png');
  };

  const saveSvg = () => {
    download(new Blob([svgText], { type: 'image/svg+xml' }), `${slugName(name)}-qr.svg`);
    flash('SVG saved');
  };

  return (
    <div className="utqr">
      <div className="utqrplate" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${qr.size} ${qr.size}`} shapeRendering="crispEdges" role="img"
          aria-label={`QR code for ${url}`}
        >
          <rect width={qr.size} height={qr.size} fill="#ffffff" />
          <path d={qr.d} fill="#000000" />
        </svg>
      </div>

      <div className="utqracts">
        <button type="button" className="btn" onClick={savePng}>
          <Icon name="down" />PNG
        </button>
        <button type="button" className="btn" onClick={saveSvg}>
          <Icon name="down" />SVG
        </button>
        {saved ? <span className="utqrok">{saved}</span> : null}
      </div>

      <p className="utnote">
        Scanning this records a click exactly as tapping the link does, so a printed campaign is
        measured by the same numbers as a digital one. Use the SVG for print — it stays sharp at any
        size.
      </p>
    </div>
  );
}

/** A filename that will not upset a file system. */
const slugName = v => String(v || 'tracking-url')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40) || 'tracking-url';
